import React, { useState, useEffect } from 'react';
import { FiPackage, FiSearch, FiPlus, FiEdit, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/api';
import { formatINR } from '../../utils/currency';
import Swal from 'sweetalert2';

const RawMaterialStock = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    currentStock: '',
    minStock: '',
    maxStock: '',
    unit: 'kg',
    supplier: '',
    costPerUnit: '',
    location: ''
  });

  useEffect(() => {
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const timeoutId = setTimeout(() => {
        searchMaterials();
      }, 500); // Debounce search
      return () => clearTimeout(timeoutId);
    } else {
      fetchMaterials();
    }
  }, [searchTerm]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.getRawMaterials, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
      Swal.fire('Error', 'Failed to fetch raw materials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const searchMaterials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.searchRawMaterials(searchTerm), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data);
    } catch (error) {
      console.error('Error searching materials:', error);
      Swal.fire('Error', 'Failed to search raw materials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const materialData = {
        ...formData,
        currentStock: parseFloat(formData.currentStock),
        minStock: parseFloat(formData.minStock),
        maxStock: parseFloat(formData.maxStock),
        costPerUnit: parseFloat(formData.costPerUnit)
      };

      if (selectedMaterial) {
        await axios.put(API_ENDPOINTS.updateRawMaterial(selectedMaterial._id), materialData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Success', 'Material updated successfully', 'success');
      } else {
        await axios.post(API_ENDPOINTS.createRawMaterial, materialData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Success', 'Material created successfully', 'success');
      }

      setShowModal(false);
      setSelectedMaterial(null);
      setFormData({
        name: '',
        code: '',
        category: '',
        currentStock: '',
        minStock: '',
        maxStock: '',
        unit: 'kg',
        supplier: '',
        costPerUnit: '',
        location: ''
      });
      fetchMaterials();
    } catch (error) {
      console.error('Error saving material:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save material';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(API_ENDPOINTS.deleteRawMaterial(id), {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Deleted', 'Material has been deleted', 'success');
        fetchMaterials();
      } catch (error) {
        console.error('Error deleting material:', error);
        const errorMessage = error.response?.data?.error || 'Failed to delete material';
        Swal.fire('Error', errorMessage, 'error');
      }
    }
  };

  const handleEdit = (material) => {
    setSelectedMaterial(material);
    setFormData({
      name: material.name,
      code: material.code,
      category: material.category,
      currentStock: material.currentStock.toString(),
      minStock: material.minStock.toString(),
      maxStock: material.maxStock.toString(),
      unit: material.unit,
      supplier: material.supplier,
      costPerUnit: material.costPerUnit.toString(),
      location: material.location
    });
    setShowModal(true);
  };

  // No need for client-side filtering since we're using API search
  const filteredMaterials = materials;

  const getStockStatus = (current, min) => {
    if (current <= min) return { color: 'red', text: 'Low Stock', icon: FiAlertCircle };
    if (current <= min * 1.5) return { color: 'yellow', text: 'Warning', icon: FiAlertCircle };
    return { color: 'green', text: 'In Stock', icon: null };
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiPackage className="text-blue-600" />
            Raw Material Stock
          </h1>
          <p className="text-gray-600 mt-1">Manage raw material inventory</p>
        </div>
        <button
          onClick={() => {
            setSelectedMaterial(null);
            setFormData({
              name: '',
              code: '',
              category: '',
              currentStock: '',
              minStock: '',
              maxStock: '',
              unit: 'kg',
              supplier: '',
              costPerUnit: '',
              location: ''
            });
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> New Material
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search materials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Material</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Code</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Current Stock</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Min Stock</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-gray-500">
                      No materials found
                    </td>
                  </tr>
                ) : (
                  filteredMaterials.map((material) => {
                    const status = getStockStatus(material.currentStock, material.minStock);
                    return (
                      <tr key={material._id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{material.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{material.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{material.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {material.currentStock} {material.unit}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {material.minStock} {material.unit}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${
                            status.color === 'red' ? 'bg-red-100 text-red-800' :
                            status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {status.icon && <status.icon />}
                            {status.text}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{material.location}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(material)}
                              className="text-green-600 hover:text-green-800"
                              title="Edit"
                            >
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(material._id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {selectedMaterial ? 'Edit Material' : 'New Material'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Material Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Material Code</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="API">API</option>
                    <option value="Excipient">Excipient</option>
                    <option value="Packaging">Packaging</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
                  <input
                    type="number"
                    value={formData.currentStock}
                    onChange={(e) => setFormData({ ...formData, currentStock: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="L">L</option>
                    <option value="mL">mL</option>
                    <option value="units">units</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Stock</label>
                  <input
                    type="number"
                    value={formData.minStock}
                    onChange={(e) => setFormData({ ...formData, minStock: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Stock</label>
                  <input
                    type="number"
                    value={formData.maxStock}
                    onChange={(e) => setFormData({ ...formData, maxStock: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <input
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Per Unit (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.costPerUnit}
                    onChange={(e) => setFormData({ ...formData, costPerUnit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedMaterial(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {selectedMaterial ? 'Update' : 'Create'} Material
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RawMaterialStock;

